import React, { useMemo, useRef, useState, useEffect } from "react";
import { Formik, Form, FieldArray, useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { FiPlusCircle, FiTrash } from "react-icons/fi";

import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { LGASelect } from "newui/components/ui/CommonSelects/LGASelect";
import { FormikAutoSave } from "newui/components/ui/AutoSave";
import FormHeader from "components/FormHeading/FormHeader";
import { Location } from "components/FormInputs/Location";

import { updateCompanyLocations, deleteCompanyAddress } from "../../company-api";
import { locationValidationSchema } from "./CompanyLocationForm.validation";
import type { Company, CompanyLocationFormValues, CompanyLocationFormValue } from "../../types";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { showToast } from "utils/helpers";
import * as constants from "actions/actionTypes";
import type { AppDispatch } from "newui/store";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import { useIsReadOnlyMode } from "newui/features/ExecutiveView";

interface CompanyLocationFormProps {
	company: Company;
}

const EMPTY_LOCATION: CompanyLocationFormValue = {
	address: "",
	latitude: null,
	longitude: null,
	city: "",
	state: "",
	postcode: "",
	local_govt_association_id: "",
};

/**
 * Syncs Location component's bracket-notation field writes
 * back into the locations array structure.
 *
 * The Location (Google Places) component uses `useFormikContext().setFieldValue`
 * internally with bracket notation (e.g. `city[0]`, `state[0]`). This component
 * watches those shadow fields and mirrors changes into our `locations` array.
 */
const LocationFieldSync: React.FC<{ index: number }> = ({ index }) => {
	const { values, setFieldValue } = useFormikContext<any>();

	const addressVal = values?.address?.[index];
	const cityVal = values?.city?.[index];
	const stateVal = values?.state?.[index];
	const postcodeVal = values?.postcode?.[index];

	const prevStateRef = useRef(values?.locations?.[index]?.state);

	useEffect(() => {
		if (addressVal !== undefined) {
			const addr = typeof addressVal === "object" ? addressVal?.value ?? "" : addressVal;
			const rawLat = typeof addressVal === "object" ? addressVal?.latitude ?? null : null;
			const rawLng = typeof addressVal === "object" ? addressVal?.longitude ?? null : null;
			const lat = rawLat != null ? String(rawLat) : null;
			const lng = rawLng != null ? String(rawLng) : null;
			setFieldValue(`locations.${index}.address`, addr, false);
			setFieldValue(`locations.${index}.latitude`, lat, false);
			setFieldValue(`locations.${index}.longitude`, lng, false);
		}
	}, [addressVal, index, setFieldValue]);

	useEffect(() => {
		if (cityVal !== undefined) {
			setFieldValue(`locations.${index}.city`, cityVal, false);
		}
	}, [cityVal, index, setFieldValue]);

	useEffect(() => {
		if (stateVal !== undefined) {
			setFieldValue(`locations.${index}.state`, stateVal, false);
			// Clear LGA if state changed
			if (prevStateRef.current && prevStateRef.current !== stateVal) {
				setFieldValue(`locations.${index}.local_govt_association_id`, "", false);
			}
			prevStateRef.current = stateVal;
		}
	}, [stateVal, index, setFieldValue]);

	useEffect(() => {
		if (postcodeVal !== undefined) {
			setFieldValue(`locations.${index}.postcode`, postcodeVal, false);
		}
	}, [postcodeVal, index, setFieldValue]);

	return null;
};

const CompanyLocationForm: React.FC<CompanyLocationFormProps> = ({ company }) => {
	const dispatch = useDispatch<AppDispatch>();
	const isReadOnly = useIsReadOnlyMode();
	const [errorStatus, setErrorStatus] = useState(false);
	const [showToastOnSave, setShowToastOnSave] = useState(false);

	const initialValues = useMemo<CompanyLocationFormValues & Record<string, any>>(() => {
		const locations: CompanyLocationFormValue[] =
			company.locations?.length > 0
				? company.locations.map((loc) => ({
						id: loc.id,
						address: loc.address || "",
						latitude: loc.latitude ?? null,
						longitude: loc.longitude ?? null,
						city: loc.city || "",
						state: loc.state || "",
						postcode: loc.postcode || "",
						local_govt_association_id: loc.local_govt_association_id || "",
					}))
				: [{ ...EMPTY_LOCATION }];

		return {
			company_id: company.id,
			locations,
			// Shadow arrays for Location component compatibility
			address: locations.map((l) => ({
				value: l.address,
				latitude: l.latitude,
				longitude: l.longitude,
			})),
			city: locations.map((l) => l.city),
			state: locations.map((l) => l.state),
			postcode: locations.map((l) => l.postcode),
		};
	}, [company]);

	const handleSubmit = async (
		values: CompanyLocationFormValues & Record<string, any>,
		{ setStatus }: { setStatus: (status: string) => void }
	) => {
		try {
			const result = await updateCompanyLocations(
				values.company_id,
				values.locations
			);

			const companyData = (result.data as any)?.data ?? result.data;

			dispatch({
				type: constants.SUBMIT_COMPANY_PROFILE_FORM,
				payload: companyData,
			});

			if (showToastOnSave) {
				showToast("Locations updated successfully.", "Success", true);
				setShowToastOnSave(false);
			}
		} catch (e: any) {
			const error = e.response?.data;
			setErrorStatus(true);
			setStatus(error?.message || "connection");

			const msg = error?.message ?? "Unexpected Error";
			if (showToastOnSave) {
				showToast(`Failed to save locations - ${msg}`);
				setShowToastOnSave(false);
			}
		}
	};

	const handleLGAChange =
		(index: number, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) =>
		(selected: SelectOption | SelectOption[] | null) => {
			const value = selected && !Array.isArray(selected) ? selected.value : "";
			setFieldValue(`locations.${index}.local_govt_association_id`, value, true);
		};

	const handleDelete = async (
		companyId: number,
		addressId: number | undefined,
		remove: (index: number) => void,
		index: number,
		setFieldValue: (field: string, value: any) => void,
		values: any
	) => {
		if (addressId) {
			try {
				await deleteCompanyAddress(companyId, addressId);
				showToast("Address has been deleted", "Success", true);
			} catch {
				showToast("Unable to delete Address", "Error");
				return;
			}
		}
		remove(index);
		// Also remove from shadow arrays
		const newAddress = [...(values.address || [])];
		const newCity = [...(values.city || [])];
		const newState = [...(values.state || [])];
		const newPostcode = [...(values.postcode || [])];
		newAddress.splice(index, 1);
		newCity.splice(index, 1);
		newState.splice(index, 1);
		newPostcode.splice(index, 1);
		setFieldValue("address", newAddress);
		setFieldValue("city", newCity);
		setFieldValue("state", newState);
		setFieldValue("postcode", newPostcode);
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={locationValidationSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, setFieldValue, isSubmitting, errors }) => (
				<Form onChange={() => setErrorStatus(false)}>
					{!isReadOnly && <FormikAutoSave debounceMs={3000} hasError={errorStatus} />}

					<fieldset disabled={isReadOnly} style={{ border: "none", padding: 0, margin: 0 }}>
					<FieldArray name="locations">
						{({ push, remove }) => (
							<div>
								{values.locations.map((location, index) => (
									<div key={location.id ?? `new-${index}`}>
										{index > 0 && (
											<hr className="my-6 border-gray-200" />
										)}

										<FormHeader
											className="mb-6"
											header={
												index === 0
													? "Your Primary Office Address"
													: index === 1
														? "Your Secondary Office Address"
														: "Your Additional Office Address"
											}
											subHeader={
												index === 0
													? "Please provide the details of your main office location."
													: "If applicable, please provide the details of any additional office locations within the state."
											}
										/>

										{/* @ts-ignore - JS component, all props optional */}
									<Location
											label="Address"
											required
											name={`address[${index}]`}
											initialValue={
												initialValues.address?.[index] ?? {
													value: "",
													latitude: null,
													longitude: null,
												}
											}
											className="!mb-6"
											rfq={true}
											inputTestId={
												index === 0
													? "primary-address-input"
													: `additional-address-input-${index}`
											}
											dropdownTestId={
												index === 0
													? "primary-address-dropdown"
													: `additional-address-dropdown-${index}`
											}
										/>

										<LocationFieldSync index={index} />

										<div className="grid grid-cols-2 gap-x-6 gap-y-4">
											<FormikInputWithLabel
												label="City / Suburb"
												name={`locations.${index}.city`}
												required
												testId={
													index === 0
														? "primary-city/suburb"
														: `additional-city/suburb-${index}`
												}
											/>
											<FormikInputWithLabel
												label="State"
												name={`locations.${index}.state`}
												required
												testId={
													index === 0
														? "primary-state"
														: `additional-state-${index}`
												}
											/>
											<FormikInputWithLabel
												label="Post Code"
												name={`locations.${index}.postcode`}
												required
												testId={
													index === 0
														? "primary-postcode"
														: `additional-postcode-${index}`
												}
											/>
											<LGASelect
												label="Local Government Area"
												name={`locations.${index}.local_govt_association_id`}
												required
												state={values.locations[index]?.state}
												selectedId={
													values.locations[index]
														?.local_govt_association_id as number | ""
												}
												onChange={handleLGAChange(index, setFieldValue)}
												testId={
													index === 0
														? "primary-local-govt-association"
														: `additional-local-govt-association-${index}`
												}
											/>
										</div>

										{index > 0 && (
											<div className="py-3">
												<ReadOnlyGuard>
													<button
														type="button"
														className="flex items-center gap-1 font-roboto text-sm font-semibold text-utility-error-700"
														data-testid="delete-address-button"
														onClick={() =>
															handleDelete(
																values.company_id,
																location.id,
																remove,
																index,
																setFieldValue,
																values
															)
														}
													>
														<FiTrash className="h-4 w-4" />
														Delete Address
													</button>
												</ReadOnlyGuard>
											</div>
										)}
									</div>
								))}

								{typeof errors.locations === "string" && (
									<div className="mt-2 rounded-md bg-red-50 p-4">
										<p className="text-sm font-medium text-red-800">
											Location Error
										</p>
										<p className="text-sm text-red-600">
											{errors.locations}
										</p>
									</div>
								)}

								<div className="mt-6 flex items-center justify-between">
									<ReadOnlyGuard>
										<button
											type="button"
											className="flex items-center gap-2 rounded bg-white px-4 py-2 text-sm text-tertiary-600"
											data-testid="add-new-address-button"
											onClick={() => {
												push({ ...EMPTY_LOCATION });
												// Also extend shadow arrays
												setFieldValue("address", [
													...(values.address || []),
													{ value: "", latitude: null, longitude: null },
												]);
												setFieldValue("city", [
													...(values.city || []),
													"",
												]);
												setFieldValue("state", [
													...(values.state || []),
													"",
												]);
												setFieldValue("postcode", [
													...(values.postcode || []),
													"",
												]);
											}}
										>
											<FiPlusCircle className="h-5 w-5" />
											Add New Address
										</button>
									</ReadOnlyGuard>

									<ReadOnlyGuard>
										<button
											type="submit"
											data-testid="submit-changes-button"
											disabled={isSubmitting}
											onClick={() => setShowToastOnSave(true)}
											className="flex h-[50px] items-center rounded-md bg-royal-blue px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
										>
											{isSubmitting ? "Saving..." : "Save Changes"}
										</button>
									</ReadOnlyGuard>
								</div>
							</div>
						)}
					</FieldArray>
					</fieldset>
				</Form>
			)}
		</Formik>
	);
};

export default CompanyLocationForm;
